import { Box, Link } from "@mui/material";
import { isString, truncate } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MdExpandLess, MdExpandMore } from "react-icons/md";

import { ReferenceFormattedText } from "./ReferenceFormattedText";

interface ReferenceCellProps {
  content: string;
  fullContent?: string;
  highlightSpans?: { start: number; end: number }[];
  fullHighlightSpans?: { start: number; end: number }[];
  maxLength?: number;
}

export const ReferenceContentCell = ({
  content,
  fullContent,
  highlightSpans = [],
  fullHighlightSpans = [],
  maxLength = 150,
}: ReferenceCellProps) => {
  const { t } = useTranslation();
  const hasHighlights = highlightSpans.length > 0;
  const [expanded, setExpanded] = useState(hasHighlights);
  const [showFull, setShowFull] = useState(false);

  const isTextLong = isString(content) && content.length > maxLength;

  const toggleExpand = () => {
    setExpanded(!expanded);
  };

  const toggleShowFull = () => {
    setShowFull(!showFull);
  };

  const getDisplayedContent = () => {
    if (showFull && fullContent) {
      // Pass original text and original spans - ReferenceFormattedText handles mapping
      return { text: fullContent, spans: fullHighlightSpans || [] };
    }

    if (expanded || !isTextLong) {
      // Pass original text and original spans
      return { text: content, spans: highlightSpans };
    }

    return {
      text: truncate(content, { length: maxLength, omission: "..." }),
      spans: [],
    };
  };

  const { text, spans } = getDisplayedContent();

  return (
    <Box>
      <ReferenceFormattedText text={text} highlightSpans={spans} />
      {isTextLong && (
        <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
          {!showFull && (
            <Link
              component="button"
              variant="body2"
              onClick={toggleExpand}
              sx={{
                textDecoration: "none",
                cursor: "pointer",
                color: "primary.main",
                display: "flex",
                alignItems: "center",
              }}
            >
              {expanded ? <MdExpandLess /> : <MdExpandMore />}
              {expanded ? t("common.showLess") : t("common.showMore")}
            </Link>
          )}
          {expanded && fullContent && (
            <Link
              component="button"
              variant="body2"
              onClick={toggleShowFull}
              sx={{
                textDecoration: "none",
                cursor: "pointer",
                color: "primary.main",
                display: "flex",
                alignItems: "center",
              }}
            >
              {showFull ? <MdExpandLess /> : <MdExpandMore />}
              {showFull
                ? t("chat.reference.hideFull")
                : t("chat.reference.showFull")}
            </Link>
          )}
        </Box>
      )}
    </Box>
  );
};
